import { useQuery } from '@tanstack/react-query';
import OCGatewayClient from '../client/OCGatewayClient';
import { TransitPolicy } from '../models/TransitPolicy';
import { normalizeTransitPolicy } from '../utils/normalizeTransitPolicy';

/**
 * Door policy assistant: a cached plain-language summary of a policy, and a
 * chat turn whose ack carries the complete reply while `doorPolicyAssistantChunk`
 * socket events stream the reply text for live rendering.
 */

export interface DoorPolicyAssistantHistoryMessage {
    role: 'user' | 'assistant';
    content: string;
}

export interface DoorPolicyAssistantQuestion {
    question: string;
    options: string[];
}

export interface DoorPolicyAssistantProposal {
    transitPolicy: TransitPolicy;
    name?: string;
    /** Plain-language description of the complete new policy, with inline tokens. */
    summary: string;
    /** What differs from the policy the user started from, with inline tokens. */
    changes: string;
}

export interface DoorPolicyAssistantReply {
    text: string;
    question?: DoorPolicyAssistantQuestion;
    proposal?: DoorPolicyAssistantProposal;
    /** `invalid_proposal` when the model could not produce a valid policy. */
    error?: string;
}

export interface DoorPolicyAssistantChatParams {
    deviceId: string;
    policyName: string | null;
    draftPolicy: TransitPolicy;
    pendingProposal?: TransitPolicy;
    messages: DoorPolicyAssistantHistoryMessage[];
    locale: string;
    /** The user tapped Not quite on the pending proposal. The message is the localised label, so this is the signal. */
    notQuite?: boolean;
}

export interface DoorPolicyAssistantStreamHandlers {
    onTextDelta?: (delta: string) => void;
    /** The assistant restarted its reply (retry after an invalid proposal): discard streamed text. */
    onReset?: () => void;
}

type ChunkEvent = { streamId: string; type: 'text-delta'; delta: string } | { streamId: string; type: 'reset' };

const activeStreams = new Map<string, DoorPolicyAssistantStreamHandlers>();
let subscribed = false;

function ensureChunkListener() {
    if (subscribed) {
        return;
    }
    subscribed = true;
    OCGatewayClient.socket.on('doorPolicyAssistantChunk', (chunk: ChunkEvent) => {
        const handlers = activeStreams.get(chunk?.streamId);
        if (!handlers) {
            return;
        }
        if (chunk.type === 'text-delta') {
            handlers.onTextDelta?.(chunk.delta);
        } else if (chunk.type === 'reset') {
            handlers.onReset?.();
        }
    });
}

/** Mint the id up front so a turn can be cancelled before its ack arrives. */
export function createDoorPolicyAssistantStreamId(): string {
    return `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;
}

/** Stable, cheap key part for a policy: the draft changes on every edit. */
export function hashPolicy(policy: unknown): string {
    const json = JSON.stringify(policy ?? null);
    let hash = 5381;
    for (let i = 0; i < json.length; i++) {
        hash = ((hash << 5) + hash + json.charCodeAt(i)) | 0;
    }
    return (hash >>> 0).toString(36);
}

export interface DoorPolicySummary {
    summary: string;
    /** Policy-specific suggested prompts for the empty-thread chips. */
    suggestions: string[];
}

export function useDoorPolicySummaryQuery(params: {
    deviceId?: string;
    policyName?: string | null;
    transitPolicy?: TransitPolicy;
    locale: string;
    enabled?: boolean;
}) {
    const { deviceId, policyName, transitPolicy, locale, enabled = true } = params;
    const policyHash = transitPolicy ? hashPolicy(transitPolicy) : undefined;

    return useQuery({
        queryKey: ['getDoorPolicySummary', deviceId, locale, policyName ?? '', policyHash],
        queryFn: async (): Promise<DoorPolicySummary> => {
            const response = (await OCGatewayClient.request('getDoorPolicySummary', {
                deviceId,
                policyName,
                transitPolicy,
                locale
            })) as { summary: string; suggestions?: string[] };
            return {
                summary: response.summary,
                suggestions: Array.isArray(response.suggestions)
                    ? response.suggestions.filter((suggestion): suggestion is string => typeof suggestion === 'string' && !!suggestion.trim())
                    : []
            };
        },
        enabled: enabled && !!deviceId && !!transitPolicy,
        // The draft changes on every edit; keep the last summary on screen
        // while the new one generates instead of collapsing to a skeleton.
        placeholderData: (previous) => previous,
        staleTime: 1000 * 60 * 60,
        gcTime: 1000 * 60 * 60 * 24,
        networkMode: 'always',
        retry: false
    });
}

export async function sendDoorPolicyAssistantMessage(
    params: DoorPolicyAssistantChatParams,
    handlers: DoorPolicyAssistantStreamHandlers = {},
    streamId: string = createDoorPolicyAssistantStreamId()
): Promise<DoorPolicyAssistantReply> {
    ensureChunkListener();
    activeStreams.set(streamId, handlers);

    try {
        const reply = (await OCGatewayClient.request('doorPolicyAssistantChat', {
            ...params,
            streamId
        })) as DoorPolicyAssistantReply;

        const proposal = reply.proposal
            ? { ...reply.proposal, transitPolicy: normalizeTransitPolicy(reply.proposal.transitPolicy)! }
            : undefined;

        return { ...reply, proposal };
    } finally {
        activeStreams.delete(streamId);
    }
}

export function cancelDoorPolicyAssistantMessage(streamId: string): Promise<unknown> {
    activeStreams.delete(streamId);
    return OCGatewayClient.request('cancelDoorPolicyAssistantChat', { streamId }).catch(() => undefined);
}
